import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import {
  LayoutDashboard, Ship, ClipboardCheck, FileText, AlertTriangle,
  BarChart3, Bell, Settings, Users, HelpCircle, Shield,
  Anchor, ChevronLeft, ChevronRight, Wrench, Building2, Grid3X3
} from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  badge?: number;
}

const mainNav: NavItem[] = [
  { label: 'Dashboard', path: '/app', icon: LayoutDashboard },
  { label: 'Fleet', path: '/app/fleet', icon: Ship },
  { label: 'Inspections', path: '/app/inspections', icon: ClipboardCheck },
  { label: 'Questionnaires', path: '/app/questionnaires', icon: Shield },
  { label: 'Reports', path: '/app/reports', icon: FileText },
  { label: 'Corrective Actions', path: '/app/capa', icon: AlertTriangle, badge: 3 },
  { label: 'Fleet Tracking', path: '/app/capa/fleet-tracking', icon: Grid3X3 },
  { label: 'Analytics', path: '/app/analytics', icon: BarChart3 },
];

const secondaryNav: NavItem[] = [
  { label: 'Notifications', path: '/app/notifications', icon: Bell },
  { label: 'Users & Roles', path: '/app/settings/users', icon: Users },
  { label: 'Company Settings', path: '/app/settings/company', icon: Building2 },
  { label: 'Help & Support', path: '/app/help', icon: HelpCircle },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, unreadCount } = useAppStore();

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full z-50 bg-[#0A1628] text-white transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'}
        lg:relative
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`h-16 flex items-center border-b border-white/10 ${sidebarCollapsed ? 'justify-center px-2' : 'px-5'}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-sky-500/20">
                <Anchor className="w-5 h-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <h1 className="text-base font-bold tracking-tight bg-gradient-to-r from-sky-300 to-blue-200 bg-clip-text text-transparent">
                    Vessel Navigator
                  </h1>
                  <p className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">Maritime Inspection</p>
                </div>
              )}
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <div className="space-y-1">
              {mainNav.map(item => {
                const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                    className={`
                      w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200
                      ${sidebarCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5'}
                      ${isActive
                        ? 'bg-sky-500/20 text-sky-300 shadow-sm'
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }
                    `}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-sky-400' : ''}`} />
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.badge && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-300 text-[10px] font-bold rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {sidebarCollapsed && item.badge && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 mb-2">
              {!sidebarCollapsed && (
                <p className="px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Management</p>
              )}
              <div className="space-y-1">
                {secondaryNav.map(item => {
                  const isActive = location.pathname.startsWith(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                      className={`
                        w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200
                        ${sidebarCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5'}
                        ${isActive
                          ? 'bg-sky-500/20 text-sky-300'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-sky-400' : ''}`} />
                      {!sidebarCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                      {!sidebarCollapsed && item.path === '/notifications' && unreadCount > 0 && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-300 text-[10px] font-bold rounded-full">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Collapse toggle */}
          <div className="hidden lg:block px-3 pb-4">
            <button
              onClick={useAppStore.getState().toggleSidebarCollapse}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all text-sm"
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              {!sidebarCollapsed && <span>Collapse</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
